// @ts-nocheck
import { useSelector } from 'react-redux';
import { Box, Divider, Typography } from '@mui/material';
import moment from 'moment';
import {
  CalendarTodayOutlined as CalendarIcon,
  QueryBuilderRounded as ClockIcon,
} from '@mui/icons-material';

import {
  FORTA_WHITE,
  FORTA_GRAY4,
  FORTA_BLUE,
  FORTA_GRAY3,
  FORTA_ERROR,
} from '../../../utils/colors';
import { isDateOlderThan24Hours, supervisionTypeStyles } from './utils';
import { areUserRolesIn, formatEntityId, formatSecondsToHHMM, history } from '../../../helpers';
import { ENTITY_PREFIX, Roles, SupervisionNoteStatus, SupervisionType } from '../../../constants';
import { ServiceCodes } from '../billings/claims/constants';
import StatusIconRenderer from '../StatusIconRenderer';
import BtIcon from '../../../assets/images/bt.svg';
import DurationIcon from '../../svgs/DurationIcon';

import type { ActionRequiredItemEnum } from '../../../constants';
import type { RootState } from '../../../store';
import type {
  IBtSupervision,
  IClientAssessment,
  IClientSupervision,
  IGroupSupervision,
  IParentSupervision,
} from '../../../utils/types';

interface ISupervisionItemProps {
  supervision:
    | IClientSupervision
    | IParentSupervision
    | IBtSupervision
    | IClientAssessment
    | IGroupSupervision;
  type: ActionRequiredItemEnum | SupervisionType;
  isActionRequiredItem?: boolean;
  sx?: any;
}

function SupervisionItem(props: ISupervisionItemProps) {
  const { user } = useSelector((state: RootState) => state.auth);
  const { type, supervision, isActionRequiredItem = false, sx = {} } = props;
  const { id: snId, date_start, status } = supervision;
  const { name, color } = supervisionTypeStyles[type] || {};
  const showBorder =
    isActionRequiredItem &&
    supervision.date_start &&
    isDateOlderThan24Hours(supervision.date_start);
  const adjustMargin =
    isActionRequiredItem &&
    (type === SupervisionType.GroupParent ||
      type === SupervisionType.Parent ||
      type === SupervisionType.Assessment);

  const duration =
    'expected_duration' in supervision ? supervision.expected_duration : supervision.duration;

  let participantDisplay: string,
    prefix = '';

  if (type === SupervisionType.GroupParent) {
    participantDisplay = `${'clients' in supervision ? (supervision.clients?.length ?? 0) : 0} families`;
  } else {
    const client = 'client' in supervision ? supervision.client : undefined;
    participantDisplay = `${client?.first_name ?? ''} ${client?.last_name ?? ''}`;
  }

  if (type === SupervisionType.Assessment) {
    prefix = ENTITY_PREFIX.ClientAssessmentNote;
  } else if (type === SupervisionType.Bt) {
    prefix = ENTITY_PREFIX.BTSupervisionNotes;
  } else if (type === SupervisionType.Client) {
    prefix = ENTITY_PREFIX.ClientSupervisionNotes;
  } else if (type === SupervisionType.Parent) {
    prefix = ENTITY_PREFIX.ParentSupervisionNotes;
  } else {
    prefix = ENTITY_PREFIX.GroupSupervisionNotes;
  }

  let statusForIcon: SupervisionNoteStatus | undefined;
  switch (status) {
    case 'STARTED':
      statusForIcon = SupervisionNoteStatus.Started;
      break;
    case 'EDITING':
      statusForIcon = SupervisionNoteStatus.Editing;
      break;
    case 'COMPLETED':
      statusForIcon = SupervisionNoteStatus.Completed;
      break;
  }

  const handleClick = () => {
    switch (type) {
      case SupervisionType.Client:
        history.navigate(`/client-supervisions/${snId}`);
        break;
      case SupervisionType.Bt:
        history.navigate(`/bt-supervisions/${snId}`);
        break;
      case SupervisionType.Parent:
        history.navigate(`/parent-supervisions/${snId}`);
        break;
      case SupervisionType.Assessment:
        history.navigate(`/assessment-note/${snId}`);
        break;
      case SupervisionType.GroupParent:
        history.navigate(`/group-parent-training/${snId}`);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        backgroundColor: FORTA_WHITE,
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'row',
        cursor: 'pointer',
        color: FORTA_GRAY4,
        minHeight: isActionRequiredItem ? '210px' : 'auto',
        border: showBorder ? `1px solid ${FORTA_ERROR}` : 'none',
        ...sx,
      }}
      onClick={handleClick}
      data-testid={`supervision-${participantDisplay.replace(/ /g, '_')}-${status}-${type}`}
    >
      {isActionRequiredItem && (
        <Box
          sx={{
            ml: 1.2,
            my: 1.5,
            width: '5px',
            backgroundColor: color,
            borderRadius: '8px',
          }}
        />
      )}
      <Box
        sx={{
          px: 2,
          py: isActionRequiredItem ? 1 : 2,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          minWidth: 230,
        }}
      >
        {isActionRequiredItem && (
          <Typography fontSize={16} fontWeight={700} mt={1} align="left">
            {type === SupervisionType.Assessment
              ? areUserRolesIn(user?.roles, [Roles.Bcba])
                ? 'Client assessment (97151)'
                : 'Assisting assessment (97152)'
              : name}
          </Typography>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mb: adjustMargin ? 2 : 0,
          }}
        >
          <StatusIconRenderer
            iconHeight={24}
            iconWidth={24}
            procedureCode={ServiceCodes.ClientSNProcedureCode}
            status={statusForIcon}
          />
          <Typography display="inline" ml={1}>
            {participantDisplay}
          </Typography>
        </Box>
        {type === SupervisionType.Client && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <img width={16} src={BtIcon} alt="BT icon" />
            <Typography display="inline" ml={0.5} fontSize={14}>
              {`${('bt_participants' in supervision
                ? supervision.bt_participants?.primary?.first_name
                : '') ?? ''} ${
                ('bt_participants' in supervision
                  ? supervision.bt_participants?.primary?.last_name
                  : '') ?? ''
              }`}
            </Typography>
          </Box>
        )}
        {type === SupervisionType.Bt && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            <img width={16} src={BtIcon} alt="BT icon" />
            <Typography display="inline" ml={0.5} fontSize={14}>
              {`${('bt_provider' in supervision ? supervision.bt_provider?.first_name : '') ?? ''} ${
                ('bt_provider' in supervision ? supervision.bt_provider?.last_name : '') ?? ''
              }`}
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            whiteSpace: 'nowrap',
            mt: adjustMargin ? 3 : 0,
          }}
        >
          <CalendarIcon sx={{ color: FORTA_BLUE.primary, height: 14, width: 14 }} />
          <Typography component="span" color={FORTA_GRAY3} fontSize={14}>
            {moment(date_start).format('MM/DD/YY')}
          </Typography>
          <Divider
            sx={{ color: FORTA_GRAY3, borderWidth: '1px', height: '16px' }}
            orientation="vertical"
          />
          <ClockIcon sx={{ color: FORTA_BLUE.primary, height: 16, width: 16 }} />
          <Typography component="span" color={FORTA_GRAY3} fontSize={14}>
            {moment(date_start).format('hh:mm a')}
          </Typography>
          <Divider
            sx={{ color: FORTA_GRAY3, borderWidth: '1px', height: '16px' }}
            orientation="vertical"
          />
          <DurationIcon color={FORTA_BLUE.primary} width={18} height={18} viewBox="0 0 13 10" />
          <Typography component="span" color={FORTA_GRAY3} fontSize={14}>
            {formatSecondsToHHMM(duration)}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 24,
            mt: 0.5,
          }}
        >
          <Typography
            fontSize={14}
            fontWeight={700}
            px={0.5}
            sx={{
              px: 1.5,
              backgroundColor: FORTA_BLUE.light3,
              borderRadius: '16px',
              color: FORTA_BLUE.primary,
              width: 'auto',
              textAlign: 'center',
            }}
          >
            {formatEntityId(snId ?? '', prefix)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default SupervisionItem;
